//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useState, useEffect, useContext } from 'react';
import { Alert, TextField, Container, Grid, Button, Typography, Divider, Box } from '@mui/material/';
import { matchPath, useLocation } from 'react-router-dom';
import { LoadingStatuses, NAVBAR_HEIGHT } from '@/constants/constants';
import axios from '../../../_utils/axios';
import { useHistory, validEmail } from '@/_utils/utils';
import { ConfigContext } from '@/contextAPI/configContext';
import { privateRoutes } from '@/view/polarisRoutes';
import { selectAuth } from '@/redux/selectors/authSelectors';
import { findFirstAccessibleRoute } from '@/_utils/routes';
import { LoginResponse, Permission } from '@/redux/slices/slice_types';
import { doLogin, getPreferences } from '@/redux/slices/authSlice';
import { getConstants, getCustomers, getLanguages } from '@/redux/slices/constantsSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Navbar from '@/components/Navbar/Navbar';
import Footer from '@/view/layout/Layout/Footer';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

type LoginValues = {
  email: string;
  password: string;
};

type LoginErrorValues = {
  email: string | null;
  password: string | null;
};

function Login() {
  const devEnv = window.location.hostname === 'localhost';
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<{ from: Location | undefined }>();
  const urlParams = new URLSearchParams(window.location.search);
  const userColors = useContext(ConfigContext)?.userColors;
  const { status } = useAppSelector(selectAuth);
  const [values, setValues] = useState<LoginValues>({ email: '', password: '' });
  const [errors, setErrors] = useState<LoginErrorValues>({ email: null, password: null });
  const navLinksConfig = useConfigValue(['navLinks']);
  const [alternativeLogin, setAlternativeLogin] = useState({
    dev_mode: true,
    login_url: null,
    prompt_text: null
  });

  useEffect(() => {
    axios.get(`/auth/login-method/`).then((res) => {
      if ((devEnv || res.data?.dev_mode) && res.data?.login_url?.length) {
        setAlternativeLogin(res.data);
      } else if (res.data?.login_url?.length) {
        if (res.data?.prompt_text?.length) {
          setAlternativeLogin((prev) => ({ ...prev, prompt_text: res.data.prompt_text }));
        }
        console.log(res.data?.login_url);
        // if the returned login url doesn't start with 'http', add it
        if (!res.data?.login_url.startsWith('http')) {
          window.location.href = '//' + res.data.login_url;
        } else {
          window.location.href = res.data.login_url;
        }
      }
    });
    if (!alternativeLogin?.login_url) {
      console.log('No alternative login url found.');
    }
  }, []);

  const validate = () => {
    const errors: LoginErrorValues = { email: null, password: null };
    const { email, password } = values;
    if (!email?.trim().length) {
      errors.email = 'Email cannot be blank.';
    }
    if (!validEmail(email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!password?.trim()) {
      errors.password = 'Password cannot be blank.';
    }
    setErrors(errors);
    return Object.values(errors).some((error) => error);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const hasErrors = validate();
    if (!hasErrors) {
      onLogin(values);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
    setErrors((prevState) => ({ ...prevState, [e.target.name]: null }));
  };

  const goToAlternativeLogin = () => {
    if (alternativeLogin?.login_url) {
      window.location.href = alternativeLogin?.login_url;
    }
  };

  const onLogin = (credentials: { email: string; password: string }) => {
    dispatch(doLogin(credentials))
      .unwrap()
      .then((res: LoginResponse) => {
        dispatch(getConstants());
        dispatch(getLanguages());

        if (!res.data?.color?.length) {
          dispatch(getPreferences(userColors));
        }

        return res;
      })
      .then((res: LoginResponse) => {
        const codes = res.data.user_permissions.map((perm: Permission) => perm.codename);

        if (codes.includes('inventory.can_view_title_customers')) {
          dispatch(getCustomers());
        }

        let redirectTo: Location | string | undefined;

        // try to redirect to the `from` location if `from` was set to the location state
        if (location.state.from) {
          const matchingRoute = privateRoutes.find(({ path, exact }) => {
            if (!location.state.from) return;
            return matchPath(location.state.from.pathname, { path, exact });
          });
          if (matchingRoute?.role && codes.includes(matchingRoute.role)) {
            redirectTo = location.state.from;
          }
        }

        redirectTo = redirectTo ?? findFirstAccessibleRoute(codes, navLinksConfig);

        if (redirectTo) {
          history.push(redirectTo);
        }
      });
  };

  return (
    <>
      <Box height={NAVBAR_HEIGHT}>
        <Navbar />
      </Box>
      <Container fixed maxWidth="xs" sx={{ pt: 5, height: 'calc(100vh - 180px)' }}>
        <Typography variant="h2" sx={{ mb: 4 }}>
          Login
        </Typography>
        <form onSubmit={onSubmit}>
          <Grid container direction="column" justifyContent="center">
            <TextField
              name="email"
              variant="filled"
              color="primary"
              label="Email"
              value={values.email}
              onChange={onInputChange}
              error={!!errors?.email}
              helperText={errors?.email}
              autoComplete="email"
              sx={{ mb: 3 }}
            />
            <TextField
              name="password"
              type="password"
              variant="filled"
              color="primary"
              label="Password"
              value={values.password}
              onChange={onInputChange}
              error={!!errors?.password}
              helperText={errors?.password}
              autoComplete="current-password"
            />
            {status === LoadingStatuses.rejected ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Login failed. Please try again.
              </Alert>
            ) : (
              ''
            )}
            <Button fullWidth type="submit" variant="contained" color="primary" size="large" sx={{ mt: 3 }}>
              Login
            </Button>
          </Grid>
          {(devEnv || alternativeLogin?.dev_mode) && (
            <>
              <Grid container alignItems="center" sx={{ my: 2 }}>
                <Grid item flex={1}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Typography variant="caption" sx={{ m: 2 }}>
                  OR
                </Typography>
                <Grid item flex={1}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              </Grid>
              <Button fullWidth variant="outlined" color="primary" size="large" onClick={goToAlternativeLogin}>
                {alternativeLogin?.prompt_text || 'Alternate Login'}
              </Button>
              {urlParams.get('alternate_login_error') && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {urlParams.get('alternate_login_error')}
                </Alert>
              )}
            </>
          )}
        </form>
      </Container>
      <Footer />
    </>
  );
}

export default Login;
